import { logger } from "@/utils/logger";
/**
 * 现代化HTTP客户端 - 基于axios和TypeScript重构
 * 提供完整的请求响应处理和错误管理
 */
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import router from "@/router";
import accessTokenManager from "@/utils/accessToken";

// HTTP错误码映射
const ERROR_MESSAGES: Record<number, string> = {
  400: "错误请求",
  401: "未授权，请重新登录",
  403: "拒绝访问",
  404: "请求错误，未找到该资源",
  405: "请求方法未允许",
  408: "请求超时",
  500: "服务器端出错",
  501: "网络未实现",
  502: "网络错误",
  503: "服务不可用",
  504: "网络超时",
  505: "HTTP版本不支持该请求",
};

// 创建axios实例
const createHttpInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: "/api",
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // 请求拦截器：自动附加Bearer access token（通过refresh换取）
  instance.interceptors.request.use(
    async (config) => {
      const url = config.url || "";
      const skipAuth = url.includes("/token/") || url.includes("/login");
      if (!skipAuth) {
        try {
          const access = await accessTokenManager.getAccessToken();
          (config.headers as any) = config.headers || {};
          (config.headers as any).Authorization = `Bearer ${access}`;
        } catch (e) {
          // 跳过，交由响应拦截器处理401
        }
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  // 响应拦截器：401时刷新access并重试一次
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response.data;
    },
    async (error: AxiosError) => {
      const config = error.config as any;
      if (error.response?.status === 401 && config && !config._retry) {
        // 🔧 修复：防止重试循环，添加_retry标记
        config._retry = true;
        try {
          accessTokenManager.clear();
          const access = await accessTokenManager.getAccessToken();
          const retryConfig: AxiosRequestConfig = { ...config };
          retryConfig.headers = {
            ...(retryConfig.headers || {}),
            Authorization: `Bearer ${access}`,
          };
          return instance.request(retryConfig);
        } catch (refreshError) {
          handleHttpError(error);
          return Promise.reject(refreshError);
        }
      }
      handleHttpError(error);
      return Promise.reject(error.response || error.message);
    },
  );

  return instance;
};

// 错误处理函数
const handleHttpError = (error: AxiosError): void => {
  if (!error.response) {
    ElMessage.error("网络连接失败，请检查网络设置");
    return;
  }

  const { status } = error.response;
  const message = ERROR_MESSAGES[status] || `连接错误 ${status}`;

  switch (status) {
    case 401:
      // 401错误特殊处理 - 跳转到登录页
      ElMessageBox.alert("未授权或登录超时，将转入登录页面", "提示", {
        confirmButtonText: "确定",
        callback: () => {
          router.push("/login");
        },
      });
      break;

    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
    case 505:
      // 服务器错误显示通知
      ElMessage.error(message);
      break;

    default:
      // 其他错误记录到控制台
      logger.error(message);
      break;
  }
};

// 创建默认实例
const http = createHttpInstance();

// 带自定义超时的请求函数
export const httpWithTimeout = (config: AxiosRequestConfig, timeout?: number): Promise<any> => {
  const requestConfig = { ...config };
  if (timeout !== undefined) {
    requestConfig.timeout = timeout;
  }
  return http(requestConfig);
};

// 便捷的请求方法
export const httpApi = {
  get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
    return http.get(url, { params, ...config });
  },

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return http.post(url, data, config);
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return http.put(url, data, config);
  },

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return http.delete(url, config);
  },

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return http.patch(url, data, config);
  },

  // 上传文件
  upload: <T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> => {
    return http.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      ...config,
    });
  },

  // 下载文件
  download: (url: string, params?: any, filename?: string): Promise<void> => {
    return http
      .get(url, {
        params,
        responseType: "blob",
      })
      .then((response: any) => {
        const blob = new Blob([response]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      });
  },
};

// HTTP状态码工具
export const HttpStatus = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  REQUEST_TIMEOUT: 408,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  HTTP_VERSION_NOT_SUPPORTED: 505,
} as const;

// 请求配置类型
export interface RequestConfig extends AxiosRequestConfig {
  showLoading?: boolean;
  showError?: boolean;
  silent?: boolean;
}

// 响应数据类型
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
  success: boolean;
}

// 创建带配置的请求实例
export const createHttpClient = (baseConfig?: AxiosRequestConfig): AxiosInstance => {
  return axios.create({
    baseURL: "/api",
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
    ...baseConfig,
  });
};

export { httpWithTimeout as axiosWithTimeout };

// 新增：导出apiClient实例（兼容apiAxios.ts）
export const apiClient = http;

// 新增：导出ApiRequestConfig类型（兼容apiAxios.ts）
export interface ApiRequestConfig extends AxiosRequestConfig {
  method?: "get" | "post" | "put" | "delete" | "patch";
  url: string;
  data?: any;
  params?: any;
  timeout?: number;
  headers?: Record<string, string>;
}

export default http;
